import React, { useCallback, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'

import { useTranslation } from '../../contexts/I18nContext'
import useLongPress from '../../hooks/useLongPress'
import useOnClickOutside from '../../hooks/useClickOutside'
import { COLORS, THEME } from '../../styles'
import { KEY_VALUES } from '../../constants'
import Icon from '../Icon'

import Styled from './RecipeFeedbackComment.styled'

const RecipeFeedbackComment = ({
  canReport,
  className,
  feedback,
  isAuthorBlocked,
  isFeedbackAuthor,
  isFeedbackBlocked,
  isLikedByUser,
  likes,
  onDeleteClick,
  onReportClick,
  recipeAuthor,
  onLikeClick,
}) => {
  const { t } = useTranslation()

  const allowReporting = canReport && !!onReportClick && !isFeedbackAuthor
  const allowDeletion = isFeedbackAuthor && !!onDeleteClick

  const [displayBubble, setDisplayBubble] = useState(false)
  const handleDisplayBubble = useCallback(() => {
    if (allowReporting || allowDeletion) {
      setDisplayBubble(true)
    }
  }, [allowDeletion, allowReporting])
  const { handlers } = useLongPress({ callback: handleDisplayBubble })

  const isHidden = isAuthorBlocked || isFeedbackBlocked

  const wrapperRef = useRef(null)
  useOnClickOutside(wrapperRef, () => setDisplayBubble(false))

  const formattedDate = useMemo(() => {
    if (!feedback.createdAt || typeof Intl === 'undefined') {
      return ''
    }
    const createdAt = DateTime.fromISO(feedback.createdAt)
    return createdAt.toRelativeCalendar()
  }, [feedback])

  const onLikeComment = useCallback(() => {
    if (!isFeedbackAuthor) {
      onLikeClick()
    }
  }, [isFeedbackAuthor, onLikeClick])

  return (
    <Styled className={className}>
      <Styled.wrapper
        displayAction={displayBubble}
        allowAction={allowReporting || allowDeletion}
        {...handlers}
        ref={wrapperRef}
      >
        <Styled.content>
          <Styled.author>
            <Styled.authorName>
              {feedback.author}
              {isFeedbackAuthor ? (
                <>
                  &nbsp;
                  <Styled.authorMe>{`(${t('ME')})`}</Styled.authorMe>
                </>
              ) : null}
            </Styled.authorName>
            {feedback.n_rating ? (
              <Styled.authorRating>
                <Styled.authorRatingIcon
                  name="star-fill-32"
                  fill={COLORS.primary}
                  width={16}
                />
                <Styled.authorRatingText>{`${feedback.n_rating}/5`}</Styled.authorRatingText>
              </Styled.authorRating>
            ) : null}
          </Styled.author>
          <Styled.description isHidden={isHidden}>
            {isAuthorBlocked ? (
              t('REPORT.BLOCK_AUTHOR_FEEDBACK')
            ) : isFeedbackBlocked ? (
              t('REPORT.HIDE_FEEDBACK')
            ) : (
              <>
                {feedback.comments}
                {formattedDate ? (
                  <Styled.descriptionDate>
                    &nbsp;&bull;&nbsp;
                    {formattedDate}
                  </Styled.descriptionDate>
                ) : null}
              </>
            )}
          </Styled.description>
          {allowReporting ? (
            <Styled.bubble
              role="button"
              tabIndex={0}
              onClick={onReportClick}
              onKeyPress={(e) => {
                if (e.key === KEY_VALUES.ENTER) {
                  onReportClick()
                }
              }}
            >
              <Styled.bubbleIcon
                name="alert-chat-bubble-16"
                width={16}
                height={16}
                fill={THEME.COLORS.foreground.primary}
              />
            </Styled.bubble>
          ) : null}
          {allowDeletion ? (
            <Styled.bubble
              role="button"
              tabIndex={0}
              onClick={onDeleteClick}
              onKeyPress={(e) => {
                if (e.key === KEY_VALUES.ENTER) {
                  onDeleteClick()
                }
              }}
            >
              <Styled.bubbleIcon
                name="delete-16"
                width={16}
                height={16}
                fill={THEME.COLORS.foreground.primary}
              />
            </Styled.bubble>
          ) : null}
        </Styled.content>
        <Styled.useful
          role={isFeedbackAuthor ? 'presentation' : 'button'}
          tabIndex={isFeedbackAuthor ? -1 : 0}
          onClick={onLikeComment}
          canLike={allowReporting}
          onKeyPress={(event) => {
            if (event.key === KEY_VALUES.ENTER) {
              onLikeComment()
            }
          }}
          isLikedByUser={isLikedByUser}
          isFeedbackAuthor={isFeedbackAuthor}
        >
          <Styled.usefulText
            isLikedByUser={isLikedByUser}
            isFeedbackAuthor={isFeedbackAuthor}
          >
            {likes > 0 || isFeedbackAuthor
              ? `${t('USEFUL')} (${likes})`
              : t('USEFUL')}
          </Styled.usefulText>
          {!isFeedbackAuthor ? (
            <Styled.usefulIcon
              name={isLikedByUser ? 'thumbs-up-fill-24' : 'thumbs-up-stroke-24'}
              width={24}
              fill={
                isLikedByUser
                  ? COLORS.primary
                  : THEME.COLORS.foreground.secondary
              }
            />
          ) : null}
        </Styled.useful>
        {feedback.jowResponse && recipeAuthor?.name ? (
          <Styled.reply>
            <Icon
              name="return-arrow-24"
              fill={THEME.COLORS.foreground.secondary}
              width={24}
              height={24}
            />
            <Styled.replyContent>
              <Styled.replyAuthor>
                <Styled.replyAuthorIcon>
                  <Icon name="j-64" fill="white" width={24} height={24} />
                </Styled.replyAuthorIcon>
                <Styled.replyAuthorText>
                  {recipeAuthor.name}
                </Styled.replyAuthorText>
              </Styled.replyAuthor>
              <Styled.replyText>{feedback.jowResponse}</Styled.replyText>
            </Styled.replyContent>
          </Styled.reply>
        ) : null}
      </Styled.wrapper>
    </Styled>
  )
}

RecipeFeedbackComment.defaultProps = {
  likes: 0,
}

export default RecipeFeedbackComment
